import { ajaxRequest, app, getAction, getActionAjax, ModalModule, t } from "@comact/crc";
import _ from "lodash";
import { IGroup, IOrganization, IPartialUser, IPermissionDetails, IPermissionKey, IUser } from "./model";
import { getUserById, getUsersByGroupId } from "./selectors";
import { actions } from "./slices";

const API_PREFIX_USER_MANAGER = "/api/v2";

/**
 * Get the current user from CMOC user manager.
 * This will be called until we receive the data
 * FIXME: Probably ready to delete
 */
// export const getMyAccount = () => async (dispatch: IStoreDispatch, getState: () => IStoreState): Promise<IMyAccount> => {
//     if (app.serverLess) {
//         const mockMyAccount = await import("./mocks").then((m) => m.getMyAccount());
//         dispatch(actions.setMyAccountFromUserManager(mockMyAccount));
//     } else if (process.env.EXEC_MODE == "icp") {
//         // For icp, myAccount will be set later in "MsalConditionalProvider"
//     }
//     return getState().myAccount;
// };

export const getMyAccountOrganizations = getActionAjax(() => (dispatch) => (
    ajaxRequest({
        serverLessResolve: () => import("./mocks").then((m) => m.getMyAccountOrganizations()),
        url: `${API_PREFIX_USER_MANAGER}/organizations`,
        onSuccess: (organizations: IOrganization[]) => {
            dispatch(actions.setMyAccountOrganizations(organizations));
            return organizations;
        },
    })
));

export const getMyAccountUsers = getActionAjax(() => (dispatch, getState) => (
    ajaxRequest({
        serverLessResolve: () => getState().myAccountUsers ? _.values(getState().myAccountUsers) : import("./mocks").then((m) => m.getMyAccountUsers()),
        url: `${API_PREFIX_USER_MANAGER}/users/my-account-users`,
        onSuccess: (users: IUser[]) => {
            dispatch(actions.setMyAccountUsers(users));
            return users;
        },
    })
));

export const getOrganizationGroups = getActionAjax(() => (dispatch, getState) => (
    ajaxRequest({
        serverLessResolve: () => getState().organizationGroups ? _.values(getState().organizationGroups) : import("./mocks").then((m) => m.getOrganizationGroups()),
        url: `${API_PREFIX_USER_MANAGER}/groups`,
        onSuccess: (groups: IGroup[]) => {
            dispatch(actions.setOrganizationGroups(groups));
            return groups;
        },
    })
));

/**
 * Since groups do not have the list of users, this function updates the group AND groupIds of each users added/removed from this group
 */
export const saveOrganizationGroupAndUsers = getAction((group: IGroup, initialGroup: IGroup, usersIds: string[], initialUserIds: string[]) => (
    async (dispatch) => {
        if (!group.id) { // is new
            const groupCreated = await dispatch(saveOrganizationGroup(group));
            dispatch(updateUsersGroups(groupCreated, usersIds));
        } else {
            const ajaxPromises: Promise<void | IGroup>[] = [];
            if (!_.isEqual(group, initialGroup)) {
                ajaxPromises.push(dispatch(saveOrganizationGroup(group)));
            }
            const addedUsers = _.difference(usersIds, initialUserIds);
            const removedUsers = _.difference(initialUserIds, usersIds);
            if (!_.isEmpty(addedUsers) || !_.isEmpty(removedUsers)) {
                ajaxPromises.push(dispatch(updateUsersGroups(group, usersIds)));
            }

            // wait until all the requests are completed
            await Promise.all(ajaxPromises);
        }
    }
));

const updateUsersGroups = getAction((group: IGroup, usersIds: string[]) => (dispatch, getState) => (
    ajaxRequest({
        method: "PUT",
        data: usersIds,
        serverLessResolve: () => import("./mocks").then((m) => m.updateOrganizationUsers(group.id, usersIds)),
        url: `${API_PREFIX_USER_MANAGER}/groups/${group.id}/users`,
        onSuccess: (updatedUsers: IUser[]) => {
            dispatch(actions.patchOrganizationUsers(updatedUsers));
            // check if the current user was added/removed from the group
            const myUser = _.find(updatedUsers, ({ accountId }) => getState().myAccount.id == accountId);
            if (myUser) dispatch(actions.patchMyAccountUsers([myUser]));
        },
    }).promise
));

const saveOrganizationGroup = getAction((group: IGroup) => (dispatch) => {
    const isNew = !group.id;
    return (
        ajaxRequest({
            method: isNew ? "POST" : "PUT",
            data: group,
            serverLessResolve: () => ({
                ...group,
                id: isNew ? _.uniqueId() : group.id,
                modificationDate: Date.now(),
                creationDate: isNew ? Date.now() : group.creationDate,
            }),
            url: `${API_PREFIX_USER_MANAGER}/groups${isNew ? "" : `/${group.id}`}`,
            onSuccess: (groupServer: IGroup) => {
                dispatch(actions.patchOrganizationGroups([groupServer]));
                return groupServer;
            },
        })
    ).promise;
});

export const deleteOrganizationGroup = getActionAjax((id: string) => (dispatch, getState) => (
    ajaxRequest({
        method: "DELETE",
        serverLessResolve: () => {
            const updatedOrganizationUsers = _.map(getUsersByGroupId({ state: getState(), groupId: id }), (user) => (
                { ...user, groupIds: _.filter(user.groupIds, (groupId) => groupId != id), modificationDate: Date.now() }
            ));
            dispatch(actions.patchMyAccountUsers(updatedOrganizationUsers));
            dispatch(actions.patchOrganizationUsers(updatedOrganizationUsers));
        },
        url: `${API_PREFIX_USER_MANAGER}/groups/${id}`,
        onSuccess: () => {
            dispatch(actions.deleteOrganizationGroups([id]));
            dispatch(getOrganizationUsers());
            dispatch(getMyAccountUsers());
        },
    })
));

/**  Only users with permission "user-management" can access organization users */
export const getOrganizationUsers = getActionAjax(() => (dispatch, getState) => (
    ajaxRequest({
        serverLessResolve: () => getState().organizationUsers ? _.values(getState().organizationUsers) : import("./mocks").then((m) => m.getOrganizationUsers()),
        url: `${API_PREFIX_USER_MANAGER}/users`,
        onSuccess: (users: IUser[]) => {
            dispatch(actions.setOrganizationUsers(users));
            return users;
        },
    })
));

/** Any user can access partial users */
export const getOrganizationPartialUsers = getActionAjax(() => (dispatch, getState) => (
    ajaxRequest({
        serverLessResolve: () => getState().organizationPartialUsers ? _.values(getState().organizationPartialUsers) : import("./mocks").then((m) => m.getOrganizationPartialUsers()),
        url: `${API_PREFIX_USER_MANAGER}/users/partial`,
        onSuccess: (users: IPartialUser[]) => {
            dispatch(actions.setOrganizationPartialUsers(users));
            return users;
        },
    })
));

export const saveOrganizationUser = getActionAjax((user: IUser) => (dispatch, getState) => {
    const isNew = !user.id;
    return (
        ajaxRequest({
            method: isNew ? "POST" : "PUT",
            data: user,
            serverLessResolve: () => ({
                ...user,
                id: isNew ? _.uniqueId() : user.id,
                modificationDate: Date.now(),
                creationDate: isNew ? Date.now() : user.creationDate,
                password: null,
            }),
            url: `${API_PREFIX_USER_MANAGER}/users${isNew ? "" : `/${user.id}`}`,
            onSuccess: (userServer: IUser) => {
                dispatch(actions.patchOrganizationUsers([userServer]));
                if (getState().myAccount.id == userServer.accountId) { // if it's the current user that is being edited
                    if (userServer.locale != app.language) { // update the app language if the current user language has been changed
                        app.changeLanguage(userServer.locale);
                    }
                    dispatch(actions.patchMyAccountUsers([userServer]));
                }
                return userServer;
            },
            onError(error) {
                const response = JSON.parse(error?.request.response);
                return ModalModule.alert({
                    title: t("userManager.users.modals.error.title"),
                    description: (
                        response?.message ?? t("userManager.users.modals.error.description")
                    ),
                });
            },
        })
    );
});

interface IPatchUser extends Partial<IUser> { id: string; }
export const patchCurrentUser = getActionAjax((partialUser: IPatchUser) => (dispatch, getState) => (
    ajaxRequest({
        method: "PATCH",
        data: partialUser,
        serverLessResolve: () => ({
            ...getState().organizationUsers[partialUser.id],
            ...partialUser,
            modificationDate: Date.now(),
        }),
        url: `${API_PREFIX_USER_MANAGER}/users/${partialUser.id}`,
        onSuccess: (userServer: IUser) => {
            dispatch(actions.patchOrganizationUsers([userServer]));
            if (getState().myAccount.id == userServer.accountId) dispatch(actions.patchMyAccountUsers([userServer]));
            return userServer;
        },
    })
));

export const restoreOrganizationUser = (user: IUser) => saveOrganizationUser({ ...user, deleted: false });

export const deleteOrganizationUser = getActionAjax((id: string) => (dispatch, getState) => (
    ajaxRequest({
        method: "PUT",
        serverLessResolve: () => ({ ...getUserById(getState(), id), modificationDate: Date.now(), groupIds: [], deleted: true }),
        url: `${API_PREFIX_USER_MANAGER}/users/delete/${id}`,
        onSuccess: (userServer: IUser) => {
            dispatch(actions.patchOrganizationUsers([userServer]));
        },
    })
));

export const getAllExistingPermissions = getActionAjax(() => (dispatch) => (
    ajaxRequest({
        serverLessResolve: () => import("./mocks").then((m) => m.getPermissions()),
        url: `${API_PREFIX_USER_MANAGER}/permissions`,
        onSuccess: (permissions: IPermissionKey[]) => {
            dispatch(actions.setPermissions(permissions));
        },
    })
));

// Only used by cmoc (oper8 still uses getAllExistingPermissions)
export const getAllPermissionsDetails = getActionAjax(() => (dispatch) => (
    ajaxRequest({
        serverLessResolve: () => import("./mocks").then((m) => m.getPermissionsDetails()),
        url: `${API_PREFIX_USER_MANAGER}/permissions/details`,
        onSuccess: (permissions: IPermissionDetails[]) => {
            dispatch(actions.setPermissionsDetails(permissions));
        },
    })
));

export const login = (credentials: object) => (
    // use a regular fetch instead of ajaxRequest because we need to handle all errors ourselves
    fetchWithTimeout("/user-manager/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
        cache: "no-cache",
        timeout: 15000,
    })
);

export const clearCache = getActionAjax(() => (_dispatch) => (
    ajaxRequest({
        method: "POST",
        serverLessResolve: () => null,
        url: "/api/v1/msal/clear-cache",
    })
));

export const logout = getActionAjax(() => (dispatch) => (
    ajaxRequest({
        serverLessResolve: () => null,
        url: "/user-manager/api/logout",
        onSuccess: () => {
            dispatch(actions.clearUserOrganizationId());
            window.location.reload();
        },
    })
));

const fetchWithTimeout = async (resource: RequestInfo | URL, options: RequestInit & { timeout?: number; } = {}) => {
    const { timeout = 30000 } = options;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    const response = await fetch(resource, {
        ...options,
        signal: controller.signal,
    });
    clearTimeout(id);
    return response;
};

export const sendInvitation = getActionAjax((userId: string) => (_dispatch) => (
    ajaxRequest({
        serverLessResolve: () => null,
        url: `/api/v2/users/invite/${userId}`,
        method: "POST",
    })
));
